import React, { useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import {
    AutocompleteWrapper,
    AutocompleteOption,
} from "../muiAutocomplete";

export interface BulkEditField {
    key: string;
    label: string;
    type: "autocomplete" | "text";
    options?: { value: string; label: string; group?: string }[];
}

interface BulkActionBarProps {
    selectedCount: number;
    fields: BulkEditField[];
    saving: boolean;
    onDelete: () => void;
    onEditApply: (fieldKey: string, value: string | null) => void;
    onClear: () => void;
}

type Step = "actions" | "selectField" | "editValue";

export function BulkActionBar({
    selectedCount,
    fields,
    saving,
    onDelete,
    onEditApply,
    onClear,
}: BulkActionBarProps) {
    const { t } = useTranslation();
    const [step, setStep] = useState<Step>("actions");
    const [activeField, setActiveField] = useState<BulkEditField | null>(null);
    const [autocompleteValue, setAutocompleteValue] =
        useState<AutocompleteOption | null>(null);
    const [textValue, setTextValue] = useState("");

    const handleSelectField = (field: BulkEditField) => {
        setActiveField(field);
        setAutocompleteValue(null);
        setTextValue("");
        setStep("editValue");
    };

    const handleApply = () => {
        if (!activeField) return;
        const value =
            activeField.type === "text"
                ? textValue
                : (autocompleteValue?.id ?? null);
        onEditApply(activeField.key, value);
        reset();
    };

    const reset = () => {
        setStep("actions");
        setActiveField(null);
        setAutocompleteValue(null);
        setTextValue("");
    };

    const autocompleteOptions: AutocompleteOption[] = (
        activeField?.options ?? []
    ).map((o) => ({ id: o.value, title: o.label, group: o.group }));
    const hasGroups = autocompleteOptions.some((o) => !!o.group);

    return (
        <Box
            sx={{
                position: "fixed",
                bottom: 24,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1300,
                minWidth: 360,
                maxWidth: 600,
            }}
        >
            <Paper
                data-testid="bulk-action-bar"
                elevation={6}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    flexWrap: "wrap",
                }}
            >
                {/* Step: actions */}
                {step === "actions" && (
                    <>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, mr: 1 }}
                        >
                            {t("workTime.bulkSelectedCount", {
                                count: selectedCount,
                            })}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={onDelete}
                            disabled={saving}
                        >
                            {t("common.remove")}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => setStep("selectField")}
                            disabled={saving}
                        >
                            {t("common.edit")}
                        </Button>
                        <Divider orientation="vertical" flexItem />
                        <IconButton
                            size="small"
                            onClick={onClear}
                            title={t("common.cancel")}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                )}

                {/* Step: select which field to edit */}
                {step === "selectField" && (
                    <>
                        <IconButton
                            size="small"
                            onClick={reset}
                            title={t("common.back")}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                            {t("workTime.bulkSelectField")}
                        </Typography>
                        {fields.map((f) => (
                            <Button
                                key={f.key}
                                size="small"
                                variant="outlined"
                                onClick={() => handleSelectField(f)}
                            >
                                {f.label}
                            </Button>
                        ))}
                        <Divider orientation="vertical" flexItem />
                        <IconButton
                            size="small"
                            onClick={onClear}
                            title={t("common.cancel")}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                )}

                {/* Step: edit the value for the selected field */}
                {step === "editValue" && activeField && (
                    <>
                        <IconButton
                            size="small"
                            onClick={() => setStep("selectField")}
                            title={t("common.back")}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, minWidth: 80 }}
                        >
                            {activeField.label}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            {activeField.type === "autocomplete" ? (
                                <AutocompleteWrapper
                                    options={autocompleteOptions}
                                    value={autocompleteValue?.id ?? null}
                                    onChange={(val: AutocompleteOption) =>
                                        setAutocompleteValue(val ?? null)
                                    }
                                    placeholder={t("common.typeToSeeOptions")}
                                    size="small"
                                    groupBy={
                                        hasGroups ? (o) => o.group : undefined
                                    }
                                    textFieldProps={{
                                        size: "small",
                                        variant: "outlined",
                                    }}
                                />
                            ) : (
                                <TextField
                                    size="small"
                                    value={textValue}
                                    onChange={(e) =>
                                        setTextValue(e.target.value)
                                    }
                                    variant="outlined"
                                    fullWidth
                                />
                            )}
                        </Box>
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={handleApply}
                            disabled={saving}
                        >
                            {saving ? (
                                <CircularProgress size={16} />
                            ) : (
                                t("common.apply")
                            )}
                        </Button>
                        <Divider orientation="vertical" flexItem />
                        <IconButton
                            size="small"
                            onClick={onClear}
                            title={t("common.cancel")}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                )}
            </Paper>
        </Box>
    );
}
